import React, { Component, Fragment } from 'react';
import classNames from 'classnames';
import autobind from 'class-autobind';
import PropTypes from 'prop-types';

import {
  ChevronCircleUpIcon,
  ChevronCircleDownIcon
} from 'zoomforth_component_lib/external_modern_icons.jsx';

import { 
  setTextLinksToDisplayExternalOpenMsg
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import SiteViewerVersionComponentSelector
  from 'zoomforth_codesbane_viewer/component_lib/site_viewer_version_component_selector_container.jsx';

const AccordionItem_ViewerVersion1 = ({
  className,
  label,
  content,
  isOpen,
  onLabelLeftClick,
  onLabelRightClick,
  onContentClick,
  labelValueReplacement,
  contentValueReplacement,
  uuid,
  isSelected,
  labelRef,
  contentRef
})=> (
  <Fragment>
    <div 
      id={uuid ? `accordion-item-${uuid}` : undefined}
      className={classNames('accordion-item', className, isOpen ? 'open' : 'closed', { 'is-selected': isSelected })}
    >
      <div ref={labelRef} className="label" >
        { 
          labelValueReplacement ? 
            <div className="left accordion-text" onClick={onLabelLeftClick}>{labelValueReplacement}</div>
          :
            <div className="left accordion-text" onClick={onLabelLeftClick} dangerouslySetInnerHTML={{ __html: label }} />
        }
        <div className="right" onClick={onLabelRightClick}>
          <div className="accordion-toggle">
            { isOpen ?
              <ChevronCircleUpIcon />
            :
              <ChevronCircleDownIcon />
            }
          </div>
        </div>
      </div>
      <div ref={contentRef} style={isOpen ? {} : { 'display': 'none' }} className="content" onClick={onContentClick}>
        { contentValueReplacement ? 
          <div className="left accordion-text" onClick={onContentClick}>{contentValueReplacement}</div>
        :
          <div className="left accordion-text" onClick={onContentClick} dangerouslySetInnerHTML={{ __html: content }} />
        }
        <div className="right">
          <div className="filler" />
        </div>
      </div>
    </div>
  </Fragment>
);


// this is specific for the editor unfortunately. when we use a button there
// then hitting the spacebar does not add a space to the text instead it
// toggles the accordion open / closed.
const AccordionLabelNode = React.forwardRef(({ shouldUseButtonForLabel, ...props }, ref) =>
  shouldUseButtonForLabel ? <button ref={ref} {...props} /> : <div ref={ref} {...props} />
);


class AccordionItem_ViewerVersion2 extends Component {

  constructor() {
    super();

    this.state = {
      isFocused: false
    };

    this.accordionRef = null;

    autobind(this);
  }

  handleAccordionRefReceived(ref) {
    this.accordionRef = ref;

    setTextLinksToDisplayExternalOpenMsg(this.accordionRef, 'a');
  }

  render() {
    const {
      uuid,
      className,
      label,
      content,
      isOpen,
      labelValueReplacement,
      contentValueReplacement,
      shouldUseButtonForLabel,
      onContentClick,
      onLabelLeftClick,
      onLabelRightClick,
      onFocus,
      onBlur,
      isSelected,
      labelRef,
      contentRef
    } = this.props;

    const { isFocused } = this.state;

    return (
      <Fragment>
        <div
          ref={this.handleAccordionRefReceived}
          id={uuid ? `accordion-item-${uuid}` : undefined}
          className={classNames('accordion-item', className, isOpen ? 'open' : 'closed', { 'is-selected': isSelected })}
        >
          <AccordionLabelNode
            ref={labelRef}
            shouldUseButtonForLabel={shouldUseButtonForLabel}
            className={classNames('label', 'accordion-trigger', {
              'is-focused': isFocused
            })}
            id={`accordion-content-label-${uuid}`}
            aria-expanded={isOpen ? 'true' : 'false'}
            aria-controls={`accordion-content-body-${uuid}`}
            onClick={onLabelRightClick}
            onFocus={()=> {
              this.setState({ isFocused: true });

              if (onFocus) {
                onFocus();
              }
            }}
            onBlur={()=> {
              this.setState({ isFocused: false });

              if (onBlur) {
                onBlur();
              }
            }}
          >
            { 
              labelValueReplacement ? 
                <div className="left accordion-text" onClick={onLabelLeftClick}>
                  {labelValueReplacement}
                </div>
              :
                <div
                  className="left accordion-text"
                  dangerouslySetInnerHTML={{ __html: label }}
                  onClick={onLabelLeftClick}
                />
            }

            <div className="right">
              <div className="accordion-toggle">
                {
                  isOpen ?
                    <ChevronCircleUpIcon />
                  :
                    <ChevronCircleDownIcon />
                }
              </div>
            </div>
          </AccordionLabelNode>

          <div
            ref={contentRef}
            role="region"
            className="content"
            hidden={isOpen ? null : true}
            id={`accordion-content-body-${uuid}`}
            aria-labelledby={`accordion-content-label-${uuid}`}
            style={isOpen ? {} : { 'display': 'none' }}
            onClick={onContentClick}
          >
            {
              contentValueReplacement ? 
              <div className="left accordion-text" onClick={onContentClick}>
                {contentValueReplacement}
              </div>
            :
              <div
                className="left accordion-text"
                dangerouslySetInnerHTML={{ __html: content }}
                onClick={onContentClick}
              />
            }

            <div className="right">
              <div className="filler" />
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

}

const AccordionItem = (props)=> (
  <SiteViewerVersionComponentSelector>
    <AccordionItem_ViewerVersion1 forSiteViewerVersion={1} { ...props } />
    <AccordionItem_ViewerVersion2 forSiteViewerVersion={2} { ...props } />
  </SiteViewerVersionComponentSelector>
);

AccordionItem.defaultProps = {
  shouldUseButtonForLabel: true
};

AccordionItem.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  content: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  labelValueReplacement: PropTypes.node,
  contentValueReplacement: PropTypes.node,
  shouldUseButtonForLabel: PropTypes.bool.isRequired,
  onLabelLeftClick: PropTypes.func.isRequired,
  onLabelRightClick: PropTypes.func.isRequired,
  onContentClick: PropTypes.func,
  uuid: PropTypes.string,
  isSelected: PropTypes.bool,
  labelRef: PropTypes.object,
  contentRef: PropTypes.object
};

export default AccordionItem;
